import { IMenuQrStepsCopy } from '@/domain/menu-qr/menu-qr-copy';
import { TMenuQrDesign } from '@/domain/menu-qr/menu-qr-design';
import { layoutMenuQr } from '@/domain/menu-qr/menu-qr-layout';
import {
  computeViewPosterLayout,
  getMenuQrPosterPxPerMm,
  MENU_QR_POSTERS,
  TMenuQrPosterSize,
} from '@/domain/menu-qr/menu-qr-poster';
import {
  createTitleMeasurer,
  drawFittedText,
  drawMenuQrOverlay,
  ensureMenuQrFonts,
  getCanvasContext,
  getMenuQrArtworkPath,
  loadOptionalImage,
  MENU_QR_FOOTER_SPRITE_PATH,
} from '@/lib/print/menu-qr-canvas';
import { createQrImage } from '@/utils';

/** Bold, like the footer labels and the artwork headline (the sticker keeps 400). */
const ORDERS_TITLE_WEIGHT = 700;

const VIEW_TITLE_COLOR = '#444444';

export interface IMenuQrPosterRenderInput {
  size: TMenuQrPosterSize;
  design: TMenuQrDesign;
  qrUrl: string;
  title: string | null;
  logoUrl: string | null;
  steps: IMenuQrStepsCopy;
  /** Reused between renders to avoid allocating a new 300 DPI canvas each time. */
  canvas?: HTMLCanvasElement;
}

/**
 * Renders one poster at print resolution: the artwork plus either the legacy
 * "menu only" overlay or the orders overlay (masked bottom text, QR, title,
 * logo, steps footer).
 */
export async function renderMenuQrPoster(
  input: IMenuQrPosterRenderInput
): Promise<HTMLCanvasElement> {
  const spec = MENU_QR_POSTERS[input.size];
  const isOrders = input.design === 'orders';

  await ensureMenuQrFonts();

  const [artwork, footerSprite, logoImage] = await Promise.all([
    loadOptionalImage(getMenuQrArtworkPath(spec.artworkFile)),
    isOrders ? loadOptionalImage(MENU_QR_FOOTER_SPRITE_PATH) : null,
    isOrders ? loadOptionalImage(input.logoUrl) : null,
  ]);

  if (!artwork) {
    throw new Error(`Could not load the poster artwork ${spec.artworkFile}.`);
  }

  const canvas = input.canvas ?? document.createElement('canvas');
  canvas.width = spec.widthPx;
  canvas.height = spec.heightPx;

  const context = getCanvasContext(canvas);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(artwork, 0, 0, canvas.width, canvas.height);

  if (isOrders) {
    const mask = spec.bottomTextMask;
    context.fillStyle = '#ffffff';
    context.fillRect(mask.x, mask.y, mask.width, mask.height);

    const layout = layoutMenuQr({
      profile: spec.ordersProfile,
      pxPerMm: getMenuQrPosterPxPerMm(input.size),
      hasLogo: !!logoImage,
      hasFooter: true,
      measureTitleWidth: createTitleMeasurer(
        context,
        input.title ?? '',
        ORDERS_TITLE_WEIGHT
      ),
    });
    const qrImage = await createQrImage(input.qrUrl, layout.qr.width);

    drawMenuQrOverlay(context, layout, {
      qrImage,
      title: input.title,
      titleWeight: ORDERS_TITLE_WEIGHT,
      logoImage,
      footerSprite,
      steps: input.steps,
    });

    return canvas;
  }

  const view = computeViewPosterLayout(input.size);
  const qrSize = Math.round(view.qr.width);
  const qrImage = await createQrImage(input.qrUrl, qrSize);

  // Whole-pixel placement without smoothing keeps the QR modules crisp.
  const previousSmoothing = context.imageSmoothingEnabled;

  context.imageSmoothingEnabled = false;
  context.drawImage(
    qrImage,
    Math.round(view.qr.x),
    Math.round(view.qr.y),
    qrSize,
    qrSize
  );
  context.imageSmoothingEnabled = previousSmoothing;

  if (input.title !== null) {
    drawFittedText(
      context,
      input.title,
      {
        centerX: view.title.centerX,
        y: view.title.baselineY,
        fontPx: view.title.fontPx,
        maxWidth: view.title.maxWidth,
      },
      900,
      'alphabetic',
      VIEW_TITLE_COLOR
    );
  }

  return canvas;
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Could not encode the poster as PNG.'));
      }
    }, 'image/png');
  });
}
